import './host'
import '@common/modules/workspaces/host'

import { ProjectService } from '@graft/nuget-gc.webportals.projects'

// import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'
import { getSessionToken, readCachedSessionTokenIfValid } from '@common/core/helpers'
import { isVisionLocal } from '@common/core/utils/isVisionLocal'
import type { WorkspaceMember, WorkspaceMemberRole } from '@common/modules/settings'

import { projectMemberToObject, projectToObject } from './object'
import type { AddProjectPayload } from './types'
import type { Project } from './types/project.types'

export async function getCurrentProject(workspaceId?: string): Promise<Project | null> {
  try {
    if (!workspaceId) {
      console.error('Workspace ID is required')
      return null
    }

    // TODO: refactor when getProjectsByWorkspaceId is ready
    const token = await getSessionToken()
    const projects = await ProjectService.getProjectsByUserId(token)

    if (projects?.length === 0) {
      return null
    }

    for (const p of projects) {
      const project = await projectToObject(p)
      if (project.workspaceId === workspaceId) {
        return project
      }
    }

    return null
  } catch (error) {
    console.error('Failed to get current project:', error)
    return null
  }
}

export async function getProjectsByUserId(): Promise<Project[]> {
  if (isVisionLocal()) {
    return []
  }

  try {
    const token = readCachedSessionTokenIfValid() ?? (await getSessionToken())
    const projects = await ProjectService.getProjectsByUserId(token)

    if (!projects || projects.length === 0) {
      return []
    }

    return await Promise.all(projects.map((p) => projectToObject(p)))
  } catch (error) {
    console.error('Failed to load projects for user:', error)
    return []
  }
}

export async function getProjectMembers(projectId: string): Promise<WorkspaceMember[]> {
  try {
    const token = await getSessionToken()
    const members = await ProjectService.getProjectMembers(token, projectId)

    if (!members || members.length === 0) {
      return []
    }

    return Promise.all(members.map((m) => projectMemberToObject(m, projectId)))
  } catch (error) {
    console.error('Failed to get project members:', error)
    throw error
  }
}

export async function updateProjectMemberLevel(
  projectId: string,
  otherUserId: string,
  role: WorkspaceMemberRole
): Promise<void> {
  try {
    const token = await getSessionToken()
    await ProjectService.updateProjectMemberLevel(token, projectId, otherUserId, role)
  } catch (error) {
    console.error('Failed to update project member level:', error)
    throw error
  }
}

export async function removeProjectMember(projectId: string, otherUserId: string): Promise<void> {
  try {
    const { deleteProjectMembership } = await import('@common/modules/memberships/memberships')
    await deleteProjectMembership(projectId, otherUserId)
  } catch (error) {
    console.error('Failed to remove project member:', error)
    throw error
  }
}

export async function inviteProjectMember(projectId: string, email: string, role: WorkspaceMemberRole): Promise<void> {
  try {
    const { detectUserRegion } = await import('@common/core/utils/detectUserRegion')
    const { inviteProjectMember: inviteProjectMemberApi } = await import('@common/modules/memberships/memberships')
    await inviteProjectMemberApi(projectId, role, email, detectUserRegion())
  } catch (error) {
    console.error('Failed to invite project member:', error)
    throw error
  }
}

/**
 * Create a new project.
 * @returns Created project id (key), or null.
 */
export async function addProject(projectData: AddProjectPayload): Promise<string | null | void> {
  try {
    const token = await getSessionToken()
    const { name, workspaceId, description } = projectData
    const createdProjectKey = await ProjectService.addProject(token, name, workspaceId, description)
    return createdProjectKey ?? null
  } catch (error) {
    console.error('Failed to create project:', error)
    throw error
  }
}

export async function deleteProject(projectId: string): Promise<void> {
  try {
    const token = await getSessionToken()
    await ProjectService.deleteProject(token, projectId)
  } catch (error) {
    console.error('Failed to delete project:', error)
    throw error
  }
}

/**
 * Update the name of a project
 */
export async function updateProjectName(projectId: string, newName: string): Promise<void> {
  try {
    const token = await getSessionToken()
    await ProjectService.updateProjectName(token, projectId, newName)
  } catch (error) {
    console.error('Failed to update project name:', error)
    throw error
  }
}

/**
 * Update the description of a project
 */
export async function updateProjectDescription(projectId: string, newDescription: string): Promise<void> {
  try {
    const token = await getSessionToken()
    await ProjectService.updateProjectDescription(token, projectId, newDescription)
  } catch (error) {
    console.error('Failed to update project description:', error)
    throw error
  }
}

/**
 * Update the logo of a project
 */
export async function updateProjectLogo(projectId: string, newLogo: number[]): Promise<void> {
  try {
    const token = await getSessionToken()
    await ProjectService.updateProjectLogo(token, projectId, newLogo)
  } catch (error) {
    console.error('Failed to update project logo:', error)
    throw error
  }
}
